import { 
  collection, 
  doc, 
  getDocs, 
  addDoc, 
  updateDoc, 
  query, 
  where,
  writeBatch
} from 'firebase/firestore'
import { db } from '../config'

export interface CostType {
  id?: string
  costCenterId: string  // ID do centro de custo pai
  code: string
  name: string
  description?: string
  active: boolean
  createdAt: string
  updatedAt: string
  organizationId: string
}

export interface CostCenter {
  id?: string
  code: string
  name: string
  description?: string
  supplierId?: string
  customerId?: string
  active: boolean
  createdAt: string
  updatedAt: string
  organizationId: string
}

export const getCostCenters = async (organizationId: string) => {
  const centersRef = collection(db, 'costCenters')
  const q = query(
    centersRef, 
    where('active', '==', true),
    where('organizationId', '==', organizationId)
  )
  const snapshot = await getDocs(q)
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as CostCenter[]
}

export const createCostCenter = async (
  organizationId: string,
  data: Omit<CostCenter, 'id' | 'active' | 'createdAt' | 'updatedAt' | 'organizationId'>
) => {
  const centersRef = collection(db, 'costCenters')
  const now = new Date().toISOString()
  
  const docRef = await addDoc(centersRef, {
    ...data,
    organizationId,
    active: true,
    createdAt: now,
    updatedAt: now
  })

  return {
    id: docRef.id,
    ...data,
    organizationId,
    active: true,
    createdAt: now,
    updatedAt: now
  }
}

export const updateCostCenter = async (id: string, data: Partial<CostCenter>) => {
  const centerRef = doc(db, 'costCenters', id)
  const updates = {
    ...data,
    updatedAt: new Date().toISOString()
  }
  
  await updateDoc(centerRef, updates)
  return { id, ...updates }
}

export const deleteCostCenter = async (id: string) => {
  const centerRef = doc(db, 'costCenters', id)
  await updateDoc(centerRef, {
    active: false,
    updatedAt: new Date().toISOString()
  })
}

// Função para migrar centros de custo de uma organização para outra
export const migrateCostCenters = async (fromOrgId: string, toOrgId: string) => {
  console.log('Iniciando migração de centros de custo:', { fromOrgId, toOrgId })
  
  try {
    // Busca todos os centros de custo da organização de origem
    const sourceCenters = await getCostCenters(fromOrgId)
    console.log('Centros de custo encontrados:', sourceCenters.length)

    // Para cada centro de custo
    for (const center of sourceCenters) {
      // Cria uma cópia do centro de custo na nova organização
      const { id: oldId, ...centerData } = center
      console.log('Migrando centro de custo:', centerData.name)

      try {
        // Cria o novo centro de custo
        const newCenter = await createCostCenter(toOrgId, {
          code: centerData.code,
          name: centerData.name,
          description: centerData.description,
          supplierId: centerData.supplierId,
          customerId: centerData.customerId
        })
        console.log('Centro de custo migrado com sucesso:', newCenter.id)

        // Busca e migra os tipos de custo
        const types = await getCostTypes(oldId)
        console.log('Tipos de custo encontrados:', types.length)

        for (const type of types) {
          const { id: oldTypeId, ...typeData } = type
          console.log('Migrando tipo de custo:', typeData.name)

          try {
            await createCostType(newCenter.id, {
              code: typeData.code,
              name: typeData.name,
              description: typeData.description,
              costCenterId: newCenter.id
            })
            console.log('Tipo de custo migrado com sucesso')
          } catch (error) {
            console.error('Erro ao migrar tipo de custo:', error)
          }
        }
      } catch (error) {
        console.error('Erro ao migrar centro de custo:', error)
      }
    }

    console.log('Migração concluída com sucesso')
    return true
  } catch (error) {
    console.error('Erro durante a migração:', error)
    throw error
  }
}

// Cost Types
export const getCostTypes = async (centerId: string) => {
  const typesRef = collection(db, `costCenters/${centerId}/types`)
  const q = query(typesRef, where('active', '==', true))
  const snapshot = await getDocs(q)
  
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  })) as CostType[]
}

export const createCostType = async (
  centerId: string, 
  data: Omit<CostType, 'id' | 'active' | 'createdAt' | 'updatedAt'>
) => {
  const typesRef = collection(db, `costCenters/${centerId}/types`)
  const now = new Date().toISOString()
  
  const docRef = await addDoc(typesRef, {
    ...data,
    active: true,
    createdAt: now,
    updatedAt: now
  })

  return {
    id: docRef.id,
    ...data,
    active: true,
    createdAt: now,
    updatedAt: now
  }
}

export const updateCostType = async (centerId: string, typeId: string, data: Partial<CostType>) => {
  const typeRef = doc(db, `costCenters/${centerId}/types/${typeId}`)
  const updates = {
    ...data,
    updatedAt: new Date().toISOString()
  }
  
  await updateDoc(typeRef, updates)
  return { id: typeId, ...updates }
}

export const deleteCostType = async (centerId: string, typeId: string) => {
  const typeRef = doc(db, `costCenters/${centerId}/types/${typeId}`)
  await updateDoc(typeRef, {
    active: false,
    updatedAt: new Date().toISOString()
  })
}